import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  omitUntouchedMeasurements,
  useMeasurementUnit,
  useMeasurementUnitReady,
  useRefillOnUnitSwitch,
} from 'hooks/useMeasurementUnit';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';
import { inViewerUnit } from 'utils/measurement';
import {
  Affix,
  Divider,
  Form,
  Input,
  InputNumber,
  PageHeader,
  Popconfirm,
  Select,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { isRegularOrg } from 'domains/orgDomain';
import { useOrg } from 'hooks/useOrg';
import { useTranslation } from 'react-i18next';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import { MeasureType } from 'domains/measurementDomain';
import { RevxMeasureTextInput } from 'components/RevxMeasureInput/RevxMeasureTextInput';
import {
  RULE_INTEGER,
  RULE_NUMBER_GT,
  RULE_MEASURE_PRECISION,
  RULE_PRECISION,
  RULE_SM_LG,
  RULE_TEXT_LEN_255,
} from 'utils/validators';

const { Option } = Select;

const EqptItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  const [form] = Form.useForm();

  const { unit, tag } = useMeasurementUnit();
  // Held until the org's system is known - see useMeasurementUnitReady. Opening this form in the
  // gap would label every measurement field with a unit that is about to change.
  const unitReady = useMeasurementUnitReady();
  const dispatch = useAppDispatch();
  const saving = useAppSelector((state) => state.loading.effects.eqpt.saveItem);
  const item = useAppSelector((state) => state.eqpt.item);
  const org = useOrg();
  const regular = org && isRegularOrg(org);

  const onClose = () => {
    dispatch.eqpt.setState({ item: undefined });
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    const updated = tag(
      omitUntouchedMeasurements(Object.assign({}, item, values), form)
    );
    await dispatch.eqpt.saveItem(updated);
    onClose();
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    const updated = tag(
      omitUntouchedMeasurements(
        // `id` is cleared here rather than after the omit: this is a create, and
        // `omitUntouchedMeasurements` keys off `id` to know there is nothing stored to leave
        // alone. Cleared afterwards, every dimension nobody retyped was dropped first.
        Object.assign({}, item, values, { id: undefined }),
        form
      )
    );
    await dispatch.eqpt.saveItem(updated);
    onClose();
  };
  const eqptTypes = useAppSelector((state) => state.eqpt.eqptTypes);

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.eqpt.removeItem(item.id);
      onClose();
    }
  };

  const specialEqptType = [
    'SW',
    'WC',
    'WCF',
    'WF',
    'WP',
    'WPC',
    'WPF',
    'WPG',
    'SDTW',
    'SHDDW',
    'SDIW',
    'DTPW',
    'HDDPW',
    'DIPW',
  ];

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            dispatch.eqpt.setState({ item: undefined });
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.eqpt.form.title.edit')
                : t('platform.eqpt.form.title.create')}
            </Headline>
          }
          subTitle={<RevxUnitSelect />}
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            ...(item?.id && !regular
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 180 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  const checkCutOffIn = (_: any, value: number) => {
    if (
      value ||
      !specialEqptType.includes(form.getFieldValue(['type', 'code']))
    ) {
      return Promise.resolve();
    }
    return Promise.reject(t('common.quantityValidate'));
  };

  useRefillOnUnitSwitch(form, item);

  return (
    <RevxSpin spinning={saving || !unitReady}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          className="platform-item-form"
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...inViewerUnit(item, unit),
          }}
          onValuesChange={() => {
            form.validateFields();
          }}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.eqptType')}
            name={['type', 'code']}
            rules={[{ required: true }]}
          >
            <Select
              disabled={Boolean(item?.id)}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {eqptTypes.map((t) => (
                <Option key={t.code} value={t.code}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('common.revxId')}
            name="revxId"
            rules={[RULE_INTEGER, { required: true }]}
          >
            <Input disabled={regular} />
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.table.columns.manufacturer')}
            name="manufacturer"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={regular} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.table.columns.printUnits')}
            name="printUnits"
          >
            <InputNumber
              min="1"
              max="16"
              precision={0}
              style={{ width: '100%' }}
              disabled={regular}
            />
          </Form.Item>

          <Form.Item label={t('platform.eqpt.form.itemLabels.avgRunSpeed')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('common.small')}
                noStyle
                name="avgRunSpeedSmall"
                rules={[RULE_NUMBER_GT(0)]}
              >
                <Input addonBefore={t('common.small')} />
              </Form.Item>
              <Form.Item
                label={t('common.large')}
                noStyle
                name="avgRunSpeedLarge"
                rules={[RULE_NUMBER_GT(0)]}
              >
                <Input addonBefore={t('common.large')} />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item label={t('platform.eqpt.form.itemLabels.click')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('platform.eqpt.form.itemLabels.bw')}
                noStyle
                name="bwClick"
                rules={[RULE_PRECISION(5)]}
              >
                <Input addonBefore={t('platform.eqpt.form.itemLabels.bw')} />
              </Form.Item>
              <Form.Item
                label={t('platform.eqpt.form.itemLabels.color')}
                noStyle
                name="colorClick"
                rules={[RULE_PRECISION(5)]}
              >
                <Input addonBefore={t('platform.eqpt.form.itemLabels.color')} />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.markup')}
            name="markupPct"
            rules={[RULE_INTEGER]}
          >
            <Input prefix="%" />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.table.columns.hourlyIncrement')}
            name="hourlyIncrement"
            rules={[RULE_PRECISION(3)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.table.columns.makereadyCost')}
            name="makereadyCostUsd"
            rules={[RULE_PRECISION(2)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.printUnitCost')}
            name="printUnitCostUsd"
            rules={[RULE_PRECISION(2)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.table.columns.plateUnitCost')}
            name="plateUnitCostUsd"
            rules={[RULE_PRECISION(3)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.setupUnitCost')}
            name="setupUnitCostUsd"
            rules={[RULE_PRECISION(3)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.washupUnitCost')}
            name="washupUnitCostUsd"
            rules={[RULE_PRECISION(3)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.inkColorCost')}
            name="inkColorSqinCostUsd"
            rules={[RULE_PRECISION(9)]}
          >
            <Input prefix="$" suffix={t('common.sqinSuffix')} />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.pmsColorCost')}
            name="pmsInkSqinCostUsd"
            rules={[RULE_PRECISION(9)]}
          >
            <Input prefix="$" suffix={t('common.sqinSuffix')} />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.metallicColorCost')}
            name="metallicInkSqinCostUsd"
            rules={[RULE_PRECISION(9)]}
          >
            <Input prefix="$" suffix={t('common.sqinSuffix')} />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.coatingCost')}
            name="coatingSqinCostUsd"
            rules={[RULE_PRECISION(9)]}
          >
            <Input prefix="$" suffix={t('common.sqinSuffix')} />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.perFoldCost')}
            name="costPerFoldUsd"
            rules={[RULE_PRECISION(2)]}
          >
            <Input prefix="$" />
          </Form.Item>
          <Form.Item
            label={t('platform.eqpt.form.itemLabels.perStapleCost')}
            name="costPerStapleUsd"
            rules={[RULE_PRECISION(2)]}
          >
            <Input prefix="$" />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.perfectorConfig')}
            name="perfectorConfig"
            rules={[
              {
                pattern: /^[1-8]{1}\/[1-8]{1}$/,
                message: t(
                  'platform.eqpt.form.validationMessages.perfectorConfig'
                ),
              },
            ]}
          >
            <Input disabled={regular} />
          </Form.Item>

          <Form.Item label={t('platform.eqpt.table.columns.caliper')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('common.min')}
                noStyle
                name="caliperMinIn"
                rules={[
                  RULE_MEASURE_PRECISION(5, MeasureType.CALIPER, form),
                  RULE_SM_LG('caliperMinIn', 'caliperMaxIn'),
                ]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.CALIPER}
                  addonBefore={t('common.min')}
                  disabled={regular}
                />
              </Form.Item>
              <Form.Item
                label={t('common.max')}
                noStyle
                name="caliperMaxIn"
                rules={[RULE_MEASURE_PRECISION(5, MeasureType.CALIPER, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.CALIPER}
                  addonBefore={t('common.max')}
                  disabled={regular}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item label={t('platform.eqpt.form.itemLabels.sheetWidth')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('common.min')}
                noStyle
                name="sheetMinWidthIn"
                rules={[
                  RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form),
                  RULE_SM_LG('sheetMinWidthIn', 'sheetMaxWidthIn'),
                ]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.min')}
                  disabled={regular}
                />
              </Form.Item>
              <Form.Item
                label={t('common.max')}
                noStyle
                name="sheetMaxWidthIn"
                rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.max')}
                  disabled={regular}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item label={t('platform.eqpt.form.itemLabels.sheetLength')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('common.min')}
                noStyle
                name="sheetMinLengthIn"
                rules={[
                  RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form),
                  RULE_SM_LG('sheetMinLengthIn', 'sheetMaxLengthIn'),
                ]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.min')}
                  disabled={regular}
                />
              </Form.Item>
              <Form.Item
                label={t('common.max')}
                noStyle
                name="sheetMaxLengthIn"
                rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.max')}
                  disabled={regular}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item label={t('platform.eqpt.table.columns.imageMax')}>
            <Input.Group compact className="flex-group">
              <Form.Item
                label={t('common.width')}
                noStyle
                name="imageMaxWidthIn"
                rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.width')}
                  disabled={regular}
                />
              </Form.Item>
              <Form.Item
                label={t('common.length')}
                noStyle
                name="imageMaxLengthIn"
                rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  addonBefore={t('common.length')}
                  disabled={regular}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.coatingUnit')}
            name="coatingUnit"
          >
            <InputNumber
              min="0"
              max="2"
              precision={0}
              style={{ width: '100%' }}
              disabled={regular}
            />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.gripper')}
            name="gripperIn"
            rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.LENGTH} disabled={regular} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.maxLift')}
            name="maxLiftLb"
            rules={[RULE_MEASURE_PRECISION(4, MeasureType.WEIGHT, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.WEIGHT} disabled={regular} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.wasteFactorPct')}
            name="wasteFactorPct"
            rules={[RULE_INTEGER]}
          >
            <Input prefix="%" />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.minWasteSheets')}
            name="minWasteSheets"
            rules={[RULE_INTEGER]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.fixedWasteSheets')}
            name="fixedWasteSheets"
            rules={[RULE_INTEGER]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.fixedWasteSheetsActive')}
            name="fixedWasteSheetsActive"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.cutOffIn')}
            name="cutOffIn"
            rules={[
              RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form),
              () => ({
                validator(_, value) {
                  return checkCutOffIn(_, value);
                },
              }),
            ]}
          >
            <RevxMeasureTextInput type={MeasureType.LENGTH} />
          </Form.Item>

          <Form.Item
            label={t('platform.eqpt.form.itemLabels.cylinderGapIn')}
            name="cylinderGapIn"
            rules={[RULE_MEASURE_PRECISION(4, MeasureType.LENGTH, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.LENGTH} />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default withMeasurementUnitScope(EqptItemForm);
